// Streams the heavy /api/Report/ExportProject job over SignalR so the UI gets
// live progress instead of blocking on one long POST.
//
// Backend hub contract (/hubs/report-export, cookie/JWT auth):
//   invoke  StartExportProject({ projectId, scenarioIds[], isShowReportDetails })
//   events  ExportProgress(jobId, percent, message)
//           ExportCompleted({ jobId, messageType, data, fileName? })
//           ExportFailed(jobId, error)
import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { environment } from 'apps/piqet-angular/src/environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { ExportProjectSummaryPayload } from './reports.service';

export interface ExportResult {
  jobId: string;
  /** 0 = success (mirrors the existing POST response shape). */
  messageType: number;
  /** Download URL for the generated file. */
  data: string;
  fileName?: string;
}

export type ExportEvent =
  | { kind: 'progress'; percent: number; message: string }
  | { kind: 'completed'; result: ExportResult };

// Completion payload for the report-generation jobs. `data` is the report already
// serialized on the server with the same settings the POST endpoints use — the
// caller JSON.parses it to get the exact object the HTTP path would have returned.
export interface ReportResult {
  jobId: string;
  messageType: number;
  message: string;
  data: string;
}

export type ReportEvent =
  | { kind: 'progress'; percent: number; message: string }
  | { kind: 'completed'; result: ReportResult };

// The hub methods that start a report-generation job.
export type ReportHubMethod =
  | 'StartFullDetailedReport'
  | 'StartPiqetCompactReport'
  | 'StartExportCsv'
  | 'StartFullISOReport'
  | 'StartExportCsvNew';

// Typed errors let the component distinguish "couldn't reach the hub"
// (-> fall back to the synchronous POST) from "the server reported a failure /
// the job timed out" (-> show an error toast, no fallback).
export class ExportConnectionFailedError extends Error {
  constructor(public readonly reason?: unknown) {
    super('Could not establish a SignalR connection for the export job.');
    this.name = 'ExportConnectionFailedError';
  }
}

export class ExportFailedError extends Error {
  constructor(message: string) {
    super(message || 'The export job failed on the server.');
    this.name = 'ExportFailedError';
  }
}

export class ExportTimeoutError extends Error {
  constructor() {
    super('The export job did not complete in time.');
    this.name = 'ExportTimeoutError';
  }
}

@Injectable({ providedIn: 'root' })
export class ReportExportSignalrService {
  private readonly signalRUrl = environment.signalRUrl;
  private readonly MAX_CONNECT_ATTEMPTS = 3;
  /** Backoff before each connect attempt (ms). First attempt is immediate. */
  private readonly CONNECT_BACKOFF_MS = [0, 1000, 2000];
  /** Rolling inactivity window — reset on every progress/heartbeat, not a total cap. */
  private readonly IDLE_TIMEOUT_MS = 60 * 1000;

  constructor(private readonly ngZone: NgZone, private readonly cookieService: CookieService) {}

  /**
   * Runs one export over SignalR. The returned Observable is cold: subscribing
   * opens the connection and starts the job; it emits `progress` events, then a
   * single `completed` event followed by completion. It errors with one of the
   * typed errors above. Unsubscribing (or completion/error) tears the
   * connection down.
   */
  exportProject(payload: ExportProjectSummaryPayload): Observable<ExportEvent> {
    return new Observable<ExportEvent>(subscriber => {
      let connection: HubConnection | null = null;
      let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
      let settled = false;

      const cleanup = () => {
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }
        if (connection) {
          connection.stop().catch(() => {
            /* stopping an already-closed connection is fine */
          });
          connection = null;
        }
      };

      const settle = (fn: () => void) => {
        if (settled) return;
        settled = true;
        this.ngZone.run(fn);
        cleanup();
      };

      // If it fires, the job/socket is dead server-side: raise a connection
      // failure so the caller falls back to the POST.
      const resetIdleTimer = () => {
        if (settled) return;
        if (timeoutHandle) clearTimeout(timeoutHandle);
        timeoutHandle = setTimeout(() => {
          settle(() =>
            subscriber.error(new ExportConnectionFailedError(new Error('Idle timeout: no progress from the export job.')))
          );
        }, this.IDLE_TIMEOUT_MS);
      };

      const run = async () => {
        connection = new HubConnectionBuilder()
          .withUrl(`${this.signalRUrl}/hubs/report-export`, {
            withCredentials: true,
            // The hub is [Authorize]'d; a browser WebSocket can't send the
            // Authorization header, so SignalR appends this as the
            // `access_token` query param for the backend to read.
            accessTokenFactory: () => this.cookieService.get('token') || '',
          })
          .withAutomaticReconnect()
          .build();

        connection.on('ExportProgress', (_jobId: string, percent: number, message: string) => {
          if (settled) return;
          resetIdleTimer();
          this.ngZone.run(() =>
            subscriber.next({
              kind: 'progress',
              percent: this.clampPercent(percent),
              message: message ?? '',
            })
          );
        });

        connection.on('ExportCompleted', (result: ExportResult) => {
          settle(() => {
            subscriber.next({ kind: 'completed', result });
            subscriber.complete();
          });
        });

        connection.on('ExportFailed', (_jobId: string, error: string) => {
          settle(() => subscriber.error(new ExportFailedError(error)));
        });

        // A close before the job settles means we lost the hub and the result
        // is no longer coming. withAutomaticReconnect handles transient drops;
        // onclose only fires once reconnection has been given up on.
        connection.onclose(err => {
          if (!settled) {
            settle(() => subscriber.error(new ExportConnectionFailedError(err)));
          }
        });

        await this.startWithRetry(connection);

        // Arm before invoke: `invoke` only resolves when the whole job ends, so a
        // timer set after it wouldn't guard the running job.
        resetIdleTimer();
        await connection.invoke('StartExportProject', payload);
      };

      run().catch(err => {
        const connErr =
          err instanceof ExportConnectionFailedError ? err : new ExportConnectionFailedError(err);
        settle(() => subscriber.error(connErr));
      });

      // Teardown on unsubscribe (e.g. component destroyed mid-export).
      return () => {
        settled = true;
        cleanup();
      };
    });
  }

  /**
   * Runs one report-generation job over SignalR. Same cold-Observable lifecycle as
   * {@link exportProject}: subscribing opens the connection and invokes `hubMethod`;
   * it emits `progress` events then a single `completed` event carrying the
   * server-serialized report. Errors with the same typed errors so the caller can
   * fall back to the existing POST on a connection failure.
   */
  generateReport(hubMethod: ReportHubMethod, payload: unknown): Observable<ReportEvent> {
    return new Observable<ReportEvent>(subscriber => {
      let connection: HubConnection | null = null;
      let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
      let settled = false;

      const cleanup = () => {
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }
        if (connection) {
          connection.stop().catch(() => {
            /* stopping an already-closed connection is fine */
          });
          connection = null;
        }
      };

      const settle = (fn: () => void) => {
        if (settled) return;
        settled = true;
        this.ngZone.run(fn);
        cleanup();
      };

      // If it fires, the job/socket is dead server-side: raise a connection
      // failure so the caller falls back to the POST.
      const resetIdleTimer = () => {
        if (settled) return;
        if (timeoutHandle) clearTimeout(timeoutHandle);
        timeoutHandle = setTimeout(() => {
          settle(() =>
            subscriber.error(new ExportConnectionFailedError(new Error('Idle timeout: no progress from the report job.')))
          );
        }, this.IDLE_TIMEOUT_MS);
      };

      const run = async () => {
        connection = new HubConnectionBuilder()
          .withUrl(`${this.signalRUrl}/hubs/report-export`, {
            withCredentials: true,
            accessTokenFactory: () => this.cookieService.get('token') || '',
          })
          .withAutomaticReconnect()
          .build();

        connection.on('ReportProgress', (_jobId: string, percent: number, message: string) => {
          if (settled) return;
          resetIdleTimer();
          this.ngZone.run(() =>
            subscriber.next({
              kind: 'progress',
              percent: this.clampPercent(percent),
              message: message ?? '',
            })
          );
        });

        connection.on('ReportCompleted', (result: ReportResult) => {
          settle(() => {
            subscriber.next({ kind: 'completed', result });
            subscriber.complete();
          });
        });

        connection.on('ReportFailed', (_jobId: string, error: string) => {
          settle(() => subscriber.error(new ExportFailedError(error)));
        });

        connection.onclose(err => {
          if (!settled) {
            settle(() => subscriber.error(new ExportConnectionFailedError(err)));
          }
        });

        await this.startWithRetry(connection);

        // Arm before invoke (see the note in exportProject).
        resetIdleTimer();
        await connection.invoke(hubMethod, payload);
      };

      run().catch(err => {
        const connErr =
          err instanceof ExportConnectionFailedError ? err : new ExportConnectionFailedError(err);
        settle(() => subscriber.error(connErr));
      });

      return () => {
        settled = true;
        cleanup();
      };
    });
  }

  private async startWithRetry(connection: HubConnection): Promise<void> {
    let lastError: unknown;
    for (let attempt = 0; attempt < this.MAX_CONNECT_ATTEMPTS; attempt++) {
      const backoff = this.CONNECT_BACKOFF_MS[attempt] ?? 2000;
      if (backoff > 0) {
        await this.delay(backoff);
      }
      try {
        await connection.start();
        return;
      } catch (err) {
        lastError = err;
      }
    }
    throw new ExportConnectionFailedError(lastError);
  }

  private clampPercent(percent: number): number {
    if (!Number.isFinite(percent)) return 0;
    return Math.min(100, Math.max(0, Math.round(percent)));
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
